// Centralizes all event listeners related to file uploads and template downloads.

import { populateCompiledDataTable } from '../data-hub/table'
import { handleSalesDataUpload, handleModalSalesDataUpload } from './sales'
import { downloadPnlTemplate, getAiPnlSuggestions } from './pnl'
import { handleTargetUpload, downloadPnlTargetTemplate, downloadSalesTargetTemplate, handleModalTargetUpload } from './targets'
import { showLoading, hideLoading, quickUploadModal } from '@/core/ui'
import { generateGeneralFinance } from '@/analysis/sections/general/finance'
import { currentUser } from '@/core/state'
import { showPnlConfirmationModal } from '@/uploads/pnl-confirmation-modal' 
import { initializeUploadToaster } from '@/components/upload-toaster'
import { FileDropZone } from './dropzone'
import { getStagedFiles, clearStagedFiles } from './dropzone'
import { getAccountContext } from '../store/api'

/**
 * Attaches all event listeners for upload buttons, template downloads, and the quick upload modal.
 */
export function initializeUploadListeners(): void {
  
  // 1. Initialize Components
  initializeUploadToaster()
  
  // Helper to safely get the current selection from the Data Hub dropdown
  // Still used for P&L and Target uploads which don't have auto-detect yet
  const getDataHubBranchSelection = () => {
    const branchSelect = document.getElementById('data-hub-branch-select') as HTMLSelectElement
    if (!branchSelect) return { id: null, name: null }
    return {
      id: branchSelect.value,
      name: branchSelect.selectedOptions[0]?.textContent || 'Unknown Branch'
    }
  }

  // --- 2. SETUP SALES DROP ZONE (Blue Theme) ---
  const salesDropZone = new FileDropZone({
    dropZoneId: 'sales-drop-zone',
    fileInputId: 'file-input',
    stagedContainerId: 'staged-files-container',
    fileListId: 'staged-files-list',
    uploadBtnId: 'upload-btn',
    theme: 'blue'
  });

  // --- 3. SETUP P&L DROP ZONE (Emerald Theme) ---
  const pnlDropZone = new FileDropZone({
    dropZoneId: 'pnl-drop-zone',
    fileInputId: 'pnl-file-input',
    stagedContainerId: 'pnl-staged-container',
    fileListId: 'pnl-staged-list',
    uploadBtnId: 'pnl-upload-btn',
    theme: 'emerald'
  });

  // --- 4. SALES UPLOAD LISTENER (AUTO-DETECT BRANCH) ---
  document.getElementById('upload-btn')?.addEventListener('click', async () => {
    
    // We do NOT check the dropdown anymore for sales
    const files = salesDropZone.getFiles()
    const typeRadio = document.querySelector('input[name="upload-type"]:checked') as HTMLInputElement
    const fileType = (typeRadio?.value || 'ESB') as 'ESB' | 'MOKA'
    
    if (files.length > 0) { 
      Array.from(files).forEach(file => {
        // Pass null for branchId/Name so the handler triggers auto-detection
        handleSalesDataUpload(file, fileType); 
      })
      salesDropZone.clear()
    } else {
      alert('Please drag and drop files first.')
    }
  })

  // --- 5. P&L UPLOAD LISTENER (SEQUENTIAL) ---
  document.getElementById('pnl-upload-btn')?.addEventListener('click', async () => {
    const { id: selectedBranchId, name: selectedBranchName } = getDataHubBranchSelection()
    const files = pnlDropZone.getFiles()

    if (!files.length || !selectedBranchId || !selectedBranchName) {
      alert('Please select a branch from the dropdown above and add P&L files.')
      return
    }

    const btn = document.getElementById('pnl-upload-btn') as HTMLButtonElement
    btn.disabled = true
    btn.textContent = 'Processing Sequence...'

    try {
      for (const file of files) {
        showLoading({ message: `Analyzing ${file.name}...` })
        const aiSuggestions = await getAiPnlSuggestions(file)
        hideLoading()

        await new Promise<void>((resolve, reject) => {
          showPnlConfirmationModal({
            fileName: file.name,
            branchId: selectedBranchId,
            branchName: aiSuggestions.branchName || selectedBranchName,
            period: aiSuggestions.period,
            suggestions: aiSuggestions.suggestions,
            onSaveComplete: async () => {
              await populateCompiledDataTable()
              resolve()
            },
          })
        })
      }

      alert('All P&L files processed successfully! The page will now refresh.')
      location.reload()

    } catch (error: any) {
      console.error(error)
      alert(`Process interrupted: ${error.message}`)
      btn.disabled = false
      btn.textContent = 'Upload P&L Files'
      hideLoading()
    }
  })

  // --- 6. OTHER LISTENERS (Refresh, Targets, etc.) ---

  document.getElementById('refresh-data-hub-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('refresh-data-hub-btn') as HTMLButtonElement
    const icon = btn.querySelector('svg')
    if (!btn || !icon) return
    btn.disabled = true
    icon.classList.add('animate-spin')
    try { await populateCompiledDataTable() } 
    catch (error) { console.error(error); alert('Error refreshing data.') } 
    finally { btn.disabled = false; icon.classList.remove('animate-spin') }
  })

  // Single P&L (Legacy)
  document.getElementById('upload-pnl-data-btn')?.addEventListener('click', async () => {
    const fileInput = document.getElementById('pnl-data-file-input') as HTMLInputElement
    const { id: selectedBranchId, name: selectedBranchName } = getDataHubBranchSelection()
    const file = fileInput.files?.[0]
    if (!file || !currentUser || !selectedBranchId || !selectedBranchName) {
      alert('Please select a branch and a P&L file.')
      return
    }
    showLoading({ message: 'Analyzing P&L file with AI...' })
    try {
      const aiSuggestions = await getAiPnlSuggestions(file)
      showLoading({ message: 'AI analysis complete. Please confirm.' })
      showPnlConfirmationModal({
        fileName: file.name,
        branchId: selectedBranchId,
        branchName: aiSuggestions.branchName || selectedBranchName,
        period: aiSuggestions.period,
        suggestions: aiSuggestions.suggestions,
        onSaveComplete: async () => {
          await populateCompiledDataTable()
          const generalKeuanganSection = document.getElementById('general-keuangan-section')
          if (generalKeuanganSection?.classList.contains('active')) {
            await generateGeneralFinance()
          }
        }
      })
    } catch (error: any) {
      alert(`Failed: ${error.message}`)
    } finally {
      hideLoading()
      fileInput.value = ''
    }
  })

  // Target Uploads
  const setupTargetUpload = (btnId: string, inputId: string, type: 'sales' | 'pnl') => {
    document.getElementById(btnId)?.addEventListener('click', async () => {
      const fileInput = document.getElementById(inputId) as HTMLInputElement
      const { id: branchId, name: branchName } = getDataHubBranchSelection()
      const file = fileInput.files?.[0]
      if (file && branchId && branchName) { 
        showLoading({ message: `Processing ${type} target...` })
        try {
          await handleTargetUpload(file, type, null, branchId, branchName)
          alert('Target uploaded successfully!')
          await populateCompiledDataTable()
        } catch (e: any) { alert(`Error: ${e.message}`) }
        finally { hideLoading(); fileInput.value = '' }
      } else { alert('Please select a file and branch.') }
    })
  }

  setupTargetUpload('upload-pnl-target-btn', 'pnl-target-file-input', 'pnl')
  setupTargetUpload('upload-sales-target-btn', 'sales-target-file-input', 'sales')

  // Template Downloads
  document.getElementById('download-pnl-template-btn')?.addEventListener('click', downloadPnlTemplate)
  document.getElementById('download-sales-target-template-btn')?.addEventListener('click', downloadSalesTargetTemplate)
  document.getElementById('download-pnl-target-template-btn')?.addEventListener('click', downloadPnlTargetTemplate)

  // Quick Upload Modal Logic
  const quickUploadConfirmBtn = document.getElementById('quick-upload-confirm-btn') as HTMLButtonElement
  quickUploadConfirmBtn?.addEventListener('click', async () => {
    if (!quickUploadModal) return
    const fileInput = document.getElementById('quick-upload-file-input') as HTMLInputElement
    const file = fileInput.files?.[0]
    const period = quickUploadModal.dataset.period
    const type = quickUploadModal.dataset.type
    const branchId = quickUploadModal.dataset.branchId
    const branchName = quickUploadModal.dataset.branchName

    if (!file || !period || !type || !branchId || !branchName) return

    quickUploadConfirmBtn.disabled = true
    
    try {
      if (type === 'salesData') {
        await handleModalSalesDataUpload(file, period, branchId)
        quickUploadModal.classList.add('hidden')
        quickUploadConfirmBtn.disabled = false
      } else {
        quickUploadConfirmBtn.textContent = 'Processing...'
        if (type === 'salesTarget') await handleModalTargetUpload(file, period, 'sales', branchId, branchName)
        else if (type === 'pnlTarget') await handleModalTargetUpload(file, period, 'pnl', branchId, branchName)
        
        await populateCompiledDataTable()
        quickUploadModal.classList.add('hidden')
        quickUploadConfirmBtn.textContent = 'Upload & Process'
        quickUploadConfirmBtn.disabled = false
      }
    } catch (e: any) {
      console.error(e)
      quickUploadConfirmBtn.disabled = false
      quickUploadConfirmBtn.textContent = 'Upload & Process'
    }
  })

  quickUploadModal?.querySelector('#quick-upload-modal-close')?.addEventListener('click', () => quickUploadModal?.classList.add('hidden'))
  quickUploadModal?.querySelector('#quick-upload-cancel-btn')?.addEventListener('click', () => quickUploadModal?.classList.add('hidden'))
}